import type { ApolloClient } from "@apollo/client";

import {
  type GetIssueQuery,
  Participant,
} from "@medbillai/generated-graphql-types";

import { errorHandling } from "../../../lib/apollo/utils";
import { getLoadingAiResponseEventId } from "./loadingEvents";
import { forgetPendingUpload, pendingUploadsFor } from "./pendingUploads";
import {
  getCaseRowsByIdQuery,
  getCaseRowsByIdQueryVariables,
  getCaseRowsSinceQuery,
  getCaseRowsSinceQueryVariables,
  getIssueQuery,
  getIssueQueryVariables,
} from "./queries";
import { isStaleRead, markReadApplied, startRead } from "./readOrder";
import type { TimelineFetch } from "./rowFetch";
import { orderEventsBySentAt } from "./streamingChatUtils";

type GetIssueIssue = NonNullable<GetIssueQuery["issue__Issue"]>[number];
type GetIssueEvent = NonNullable<GetIssueIssue["userVisibleEvents"]>[number];

/**
 * An issue read by one of the narrow queries. Its events must assign to
 * GetIssue's own event type, which turns a selection that drifted from
 * GetIssue's into a compile error where the read is merged.
 */
export type NarrowIssue = Partial<Omit<GetIssueIssue, "userVisibleEvents">> & {
  readonly userVisibleEvents?: readonly GetIssueEvent[] | null;
};

export type MergeResult =
  | { readonly complete: true; readonly data: GetIssueQuery }
  | { readonly complete: false };

const INCOMPLETE: MergeResult = { complete: false };

/**
 * Newest server `sentAt` in the cached transcript, used as the tail's
 * lower bound. The streamed placeholder is stamped client-side, so
 * including it would skip every event the turn actually wrote. `excludeIds`
 * are rows read by id that may be newer than rows that went missing. The
 * member's rows are left out too: the client writes them itself, from the
 * send and the upload, so they say nothing about the rows before them.
 */
export const newestCachedSentAt = (
  cached: GetIssueQuery | null | undefined,
  loadingEventId: string,
  excludeIds: ReadonlySet<string> = new Set(),
): string | null => {
  const events = cached?.issue__Issue?.[0]?.userVisibleEvents ?? [];
  let newest: string | null = null;
  let newestMs = Number.NEGATIVE_INFINITY;
  for (const event of events) {
    if (
      event.id === loadingEventId ||
      excludeIds.has(event.id) ||
      event.participant === Participant.USER
    ) {
      continue;
    }
    const parsed = Date.parse(event.sentAt);
    if (Number.isNaN(parsed) || parsed < newestMs) {
      continue;
    }
    newest = event.sentAt;
    newestMs = parsed;
  }
  return newest;
};

/**
 * Fold a narrow read into the cached GetIssue data: add new ids, keep sentAt
 * order, refresh the issue fields, and drop the streamed placeholder once the
 * reply it stands in for has landed. A fresh read also replaces the rows it
 * names; a stale one only adds rows the cache lacks. A pending upload leaves in
 * the cache write that adds its own document's row, so both never render together.
 */
export const mergeNarrowIssue = ({
  cached,
  narrow,
  loadingEventId,
  dropsLoadingEvent,
  refreshesKnownRows = false,
  pendingUploads = new Map(),
}: {
  cached: GetIssueQuery;
  narrow: NarrowIssue | undefined;
  loadingEventId: string;
  dropsLoadingEvent: boolean;
  refreshesKnownRows?: boolean;
  pendingUploads?: ReadonlyMap<string, string | undefined>;
}): MergeResult => {
  const cachedIssue = cached.issue__Issue?.[0];
  if (!cachedIssue || !narrow || narrow.id !== cachedIssue.id) {
    return INCOMPLETE;
  }
  const { userVisibleEvents, ...issueFields } = narrow;

  const readRows = new Map(
    (userVisibleEvents ?? []).map(event => [event.id, event]),
  );
  const cachedIds = new Set(
    (cachedIssue.userVisibleEvents ?? []).map(event => event.id),
  );
  const landedDocuments = new Set(
    (userVisibleEvents ?? []).flatMap(event =>
      event.__typename === "issue__DocumentUploadedEvent_Type" &&
      event.participant === Participant.USER &&
      !cachedIds.has(event.id) &&
      event.document
        ? [event.document.id]
        : [],
    ),
  );
  const replacedPending = new Set(
    [...pendingUploads].flatMap(([id, documentId]) =>
      cachedIds.has(id) &&
      documentId !== undefined &&
      landedDocuments.has(documentId)
        ? [id]
        : [],
    ),
  );
  const kept = (cachedIssue.userVisibleEvents ?? [])
    .filter(event => !dropsLoadingEvent || event.id !== loadingEventId)
    .filter(event => !replacedPending.has(event.id))
    .map(event =>
      refreshesKnownRows ? (readRows.get(event.id) ?? event) : event,
    );
  const knownIds = new Set(kept.map(event => event.id));
  const appended = (userVisibleEvents ?? []).filter(event => {
    if (knownIds.has(event.id)) {
      return false;
    }
    knownIds.add(event.id);
    return true;
  });

  const mergedIssue: GetIssueIssue = {
    ...cachedIssue,
    ...issueFields,
    userVisibleEvents: [...orderEventsBySentAt([...kept, ...appended])],
  };

  return {
    complete: true,
    data: {
      ...cached,
      issue__Issue: [mergedIssue, ...(cached.issue__Issue ?? []).slice(1)],
    },
  };
};

type CaseRead = {
  client: ApolloClient<unknown>;
  issueId: string;
  userId: string;
};

/**
 * Rows the by-id path merged since the last read that covered them: a tail
 * read, or a fresh full read. A row read by id says nothing about the rows
 * before it, so none of these may anchor the next tail, whichever plan asks
 * for it.
 */
const readByIdSinceCoveringRead = new Map<string, Set<string>>();

/** A fresh full read holds every row applied before it, so none can be skipped. */
export const forgetRowsReadById = (issueId: string): void => {
  readByIdSinceCoveringRead.delete(issueId);
};

/**
 * A narrow read that has not answered by then has failed, so the fallback
 * runs instead of waiting on a request that may never settle.
 */
export const NARROW_READ_DEADLINE_MS = 10_000;

export const withinReadDeadline = <T>(read: Promise<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("Transcript read passed its deadline")),
      NARROW_READ_DEADLINE_MS,
    );
    read.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

const readCachedIssue = ({ client, issueId, userId }: CaseRead) =>
  client.cache.readQuery({
    query: getIssueQuery,
    variables: getIssueQueryVariables(issueId, userId),
  });

/**
 * Fold into the GetIssue entry inside the update, since the cache can move
 * while a read is in flight. False when nothing is cached or the read does not
 * fit. A null `readNumber` is a cache-only write, which takes no place in the
 * read order.
 */
const writeIntoGetIssue = (
  { client, issueId, userId }: CaseRead,
  narrow: NarrowIssue | undefined,
  dropsLoadingEvent: boolean,
  readNumber: number | null,
): boolean => {
  const loadingEventId = getLoadingAiResponseEventId(issueId);
  // The reads bypass the cache, so only this ordered write can land them.
  const stale = readNumber !== null && isStaleRead(issueId, readNumber);
  const fitted =
    stale && narrow
      ? { id: narrow.id, userVisibleEvents: narrow.userVisibleEvents }
      : narrow;
  let merged = false;
  let remaining: ReadonlySet<string> = new Set();
  client.cache.updateQuery(
    {
      query: getIssueQuery,
      variables: getIssueQueryVariables(issueId, userId),
    },
    current => {
      if (!current) {
        return undefined;
      }
      const outcome = mergeNarrowIssue({
        cached: current,
        narrow: fitted,
        loadingEventId,
        dropsLoadingEvent,
        refreshesKnownRows: !stale,
        pendingUploads: pendingUploadsFor(issueId),
      });
      if (!outcome.complete) {
        return undefined;
      }
      merged = true;
      remaining = new Set(
        (outcome.data.issue__Issue?.[0]?.userVisibleEvents ?? []).map(
          event => event.id,
        ),
      );
      return outcome.data;
    },
  );
  for (const id of [...pendingUploadsFor(issueId).keys()]) {
    if (merged && !remaining.has(id)) {
      forgetPendingUpload(issueId, id);
    }
  }
  if (merged && !stale && readNumber !== null) {
    markReadApplied(issueId, readNumber);
  }
  return merged;
};

/**
 * Read the named rows in one request and fold them in with one cache update.
 * False means fall back: a failed read, or a named row that did not come back.
 */
export const applyEventsById = async (
  read: CaseRead & {
    eventIds: readonly string[];
    dropsLoadingEvent: boolean;
  },
): Promise<boolean> => {
  const { client, issueId, userId, eventIds, dropsLoadingEvent } = read;
  const readNumber = startRead(issueId);
  let issue: NarrowIssue | null | undefined;
  try {
    const { data } = await withinReadDeadline(
      client.query({
        query: getCaseRowsByIdQuery,
        variables: getCaseRowsByIdQueryVariables(issueId, userId, eventIds),
        fetchPolicy: "no-cache",
        context: errorHandling("caller"),
      }),
    );
    issue = data.caseRowsById;
  } catch {
    return false;
  }
  const returned = new Set((issue?.userVisibleEvents ?? []).map(row => row.id));
  if (!issue || eventIds.some(id => !returned.has(id))) {
    return false;
  }
  const merged = writeIntoGetIssue(read, issue, dropsLoadingEvent, readNumber);
  if (merged) {
    const readIds = readByIdSinceCoveringRead.get(issueId) ?? new Set<string>();
    for (const id of eventIds) {
      readIds.add(id);
    }
    readByIdSinceCoveringRead.set(issueId, readIds);
  }
  return merged;
};

/** Read everything after the last contiguous row and fold it in. */
export const applyTurnTail = async (
  read: CaseRead & {
    excludeIds: readonly string[];
    dropsLoadingEvent: boolean;
  },
): Promise<boolean> => {
  const { client, issueId, userId, excludeIds, dropsLoadingEvent } = read;
  // Rows read by id while this tail is in flight may be newer than what it
  // returns, so only the ones it anchored below stop being excluded.
  const anchoredBelow = [...(readByIdSinceCoveringRead.get(issueId) ?? [])];
  const after = newestCachedSentAt(
    readCachedIssue(read),
    getLoadingAiResponseEventId(issueId),
    new Set([...excludeIds, ...anchoredBelow]),
  );
  if (!after) {
    return false;
  }
  const readNumber = startRead(issueId);
  try {
    const { data } = await withinReadDeadline(
      client.query({
        query: getCaseRowsSinceQuery,
        variables: getCaseRowsSinceQueryVariables(issueId, userId, after),
        fetchPolicy: "no-cache",
        context: errorHandling("caller"),
      }),
    );
    const merged = writeIntoGetIssue(
      read,
      data.caseRowsSince ?? undefined,
      dropsLoadingEvent,
      readNumber,
    );
    if (merged) {
      const readIds = readByIdSinceCoveringRead.get(issueId);
      for (const id of anchoredBelow) {
        readIds?.delete(id);
      }
    }
    return merged;
  } catch {
    return false;
  }
};

/**
 * Run a planned batch against the cached transcript: the named rows by id,
 * then the since-last-seen read when the plan or a failed id read calls for
 * it. Resolves false when the caller should run the full GetIssue refetch.
 */
export const applyTimelineFetch = async ({
  fetch,
  ...read
}: CaseRead & { fetch: TimelineFetch }): Promise<boolean> => {
  const { dropsLoadingEvent } = fetch;
  let sinceLastSeen = fetch.sinceLastSeen;
  if (fetch.eventIds.length > 0) {
    const merged = await applyEventsById({
      ...read,
      eventIds: fetch.eventIds,
      dropsLoadingEvent,
    });
    if (!merged) {
      sinceLastSeen = sinceLastSeen ?? { excludeIds: [] };
    }
  }
  if (sinceLastSeen) {
    // The batch's own by-id rows run first and cannot anchor the tail.
    return applyTurnTail({
      ...read,
      excludeIds: [...sinceLastSeen.excludeIds, ...fetch.eventIds],
      dropsLoadingEvent,
    });
  }
  if (fetch.eventIds.length === 0 && dropsLoadingEvent) {
    // A turn that wrote nothing new still retires its placeholder, no read.
    const cachedIssueId = readCachedIssue(read)?.issue__Issue?.[0]?.id;
    if (cachedIssueId) {
      // Reads nothing, so an in-flight read must not count as stale behind it.
      writeIntoGetIssue(read, { id: cachedIssueId }, true, null);
    }
  }
  return true;
};
